import { useTranslation } from "react-i18next";

import type { RobotAudience } from "@/entities/robot";
import { formatMoney, formatPercent, staggerIndex } from "@/shared/lib";
import { Avatar, Panel } from "@/shared/ui";

/**
 * ТОП-3 пользователей робота (доработки §5).
 *
 * Свой виджет, а не ветка `AudienceBlock`: у подиума появились аватары, ранги,
 * две величины и подпись периода — с «Под управлением» и «Средним временем
 * использования» его роднит только соседство на экране.
 *
 * **Личность приходит с сервера уже решённой.** У участника, давшего согласие
 * (`is_public`), в строке настоящее имя и фотография; у остальных — ярлык
 * «Трейдер #1842» и заглушка без единой буквы имени. Клиент этот выбор не
 * делает и делать не может: он не видит ни ника, ни telegram_id — их просто
 * нет в ответе.
 *
 * **Подпись периода обязательна.** Окно считает оператор, и без подписи смена
 * настройки в админке выглядит как «результаты изменились сами».
 *
 * Виджет не рисуется вовсе, когда `top_results` пуст: это либо «покупателей
 * меньше порога», либо «за период никто не вышел в плюс». Подиум из нулей —
 * та же ноль-заглушка, только с медалями.
 */
const MEDALS = ["🥇", "🥈", "🥉"];

export function RobotLeaderboard({ audience }: { audience: RobotAudience }) {
  const { t } = useTranslation();
  const rows = audience.top_results;

  if (!rows || rows.length === 0) return null;

  return (
    <Panel as="section" radius="lg" className="flex flex-col gap-3 p-4">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-body font-semibold text-text-primary">
          {t("robots.analytics.audience.topTitle")}
        </h2>
        {audience.top_period ? (
          <span className="text-micro font-normal text-text-tertiary">
            {t(`robots.analytics.audience.topPeriod.${audience.top_period}`)}
          </span>
        ) : null}
      </header>

      <ol className="stagger-children flex flex-col gap-2">
        {rows.map((row, index) => (
          <li
            key={row.rank}
            className={[
              "stagger flex min-w-0 items-center gap-2.5 rounded-xl px-2 py-1.5",
              row.is_me ? "bg-surface-selected" : "",
            ].join(" ")}
            style={staggerIndex(index)}
          >
            <span className="w-5 shrink-0 text-center text-caption" aria-hidden="true">
              {MEDALS[index] ?? row.rank}
            </span>

            <Avatar src={row.avatar_url ?? null} initial={row.initial} size={28} />

            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-caption font-medium text-text-primary">
                {row.display_name}
              </span>
              {row.is_public ? null : (
                <span className="truncate text-micro font-normal text-text-tertiary">
                  {t("robots.analytics.audience.topAnonymous")}
                </span>
              )}
            </span>

            <span className="flex shrink-0 flex-col items-end">
              <span className="text-caption font-semibold text-status-profit tabular-nums">
                {formatMoney(row.earned, { sign: "always", suffix: "USDT" })}
              </span>
              <span className="text-micro font-normal text-text-tertiary tabular-nums">
                {formatPercent(row.earned_pct, { sign: "always" })}
              </span>
            </span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}
